import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SearchIcon from '@mui/icons-material/Search';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import api from '../api/api';
import { formatPortalDate } from '../utils/dateFormat';
import { InspectionReportDialog } from './TruckInspectionLogs';

function rowSearchText(row) {
  return [
    row.inspection?.issues,
    row.inspection?.inspected_by,
    row.resolved_by,
    row.reporter_email,
    row.email_recipient,
    row.truck?.name,
    row.truck?.license_plate,
    row.truck?.make,
    row.truck?.model,
  ].filter(Boolean).join(' ').toLowerCase();
}

export default function ResolvedDefectsTable() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [emailFilter, setEmailFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState(null);

  const loadRows = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get('/mechanic-defects/resolved');
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setError('');
    } catch (exception) {
      setError(exception?.response?.data?.error || 'Unable to load resolved defect history.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
    const refresh = () => document.visibilityState === 'visible' && loadRows(true);
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('dataUpdated', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('dataUpdated', refresh);
    };
  }, [loadRows]);

  const sentCount = items.filter((row) => row.email_sent).length;
  const filteredRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((row) => {
      if (term && !rowSearchText(row).includes(term)) return false;
      if (emailFilter === 'sent') return row.email_sent;
      if (emailFilter === 'not_sent') return !row.email_sent;
      return true;
    });
  }, [emailFilter, items, query]);
  const visibleRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [query, emailFilter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredRows.length, page, rowsPerPage]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Paper className="portal-page-header" sx={{ p: { xs: 2, md: 2.5 }, mb: 2.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 46, height: 46, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: 'rgba(53,109,89,.12)' }}>
              <TaskAltIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>Resolved Defects</Typography>
              <Typography variant="body2" color="text.secondary">Resolution history and reporter-email delivery status.</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
            <Chip label={`${items.length} resolved`} sx={{ bgcolor: '#e8f7ef', color: '#18734b', fontWeight: 900 }} />
            <Chip icon={<MarkEmailReadOutlinedIcon />} label={`${sentCount} emails sent`} sx={{ bgcolor: '#e8f0f8', color: '#315f7d', fontWeight: 900 }} />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.75 }, mb: 2, borderColor: '#dce5ee' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(240px, 1fr) 190px' }, gap: 1.25 }}>
          <TextField
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search vehicle, defect, reporter, mechanic, or email"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TextField select size="small" label="Email status" value={emailFilter} onChange={(event) => setEmailFilter(event.target.value)}>
            <MenuItem value="all">All email statuses</MenuItem>
            <MenuItem value="sent">Email sent</MenuItem>
            <MenuItem value="not_sent">Email not sent</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden', borderColor: '#d8e3ec' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  {['Vehicle', 'Defect', 'Reported By', 'Resolved By', 'Resolved At', 'Reporter Email'].map((label) => (
                    <TableCell key={label} sx={{ bgcolor: '#f4f8f6', color: '#244d3e', fontWeight: 900 }}>{label}</TableCell>
                  ))}
                  <TableCell align="center" sx={{ bgcolor: '#f4f8f6', color: '#244d3e', fontWeight: 900 }}>Email Sent?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!visibleRows.length && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center' }}>
                      <TaskAltIcon sx={{ fontSize: 38, color: '#87ad9b', mb: .75 }} />
                      <Typography sx={{ fontWeight: 900 }}>No resolved defects found</Typography>
                      <Typography variant="body2" color="text.secondary">Try changing the search or email-status filter.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visibleRows.map((row) => (
                  <TableRow
                    hover
                    key={row.inspection.id}
                    tabIndex={0}
                    onClick={() => setSelected(row.inspection)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelected(row.inspection);
                      }
                    }}
                    sx={{ cursor: 'pointer', '&:nth-of-type(even)': { bgcolor: '#fbfdfc' }, '&:focus-visible': { outline: '2px solid #356d59', outlineOffset: -2 } }}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 900, color: '#16324f' }}>{row.truck?.name || `Vehicle #${row.inspection.truck_id}`}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.truck?.license_plate || 'No plate'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 310 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden' }}>
                        {row.inspection.issues || 'No defect description provided.'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.inspection.inspected_by || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.resolved_by || 'Not recorded'}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatPortalDate(row.resolved_at, { includeTime: true })}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{row.email_recipient || row.reporter_email || 'No email on account'}</Typography>
                      {row.email_sent_at && <Typography variant="caption" color="text.secondary">Sent {formatPortalDate(row.email_sent_at, { includeTime: true })}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={row.email_sent ? <MarkEmailReadOutlinedIcon /> : <EmailOutlinedIcon />}
                        label={row.email_sent ? 'Sent' : 'Not sent'}
                        sx={{ bgcolor: row.email_sent ? '#e8f7ef' : '#fff1f1', color: row.email_sent ? '#18734b' : '#a23b46', fontWeight: 900 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 150]}
          />
        </Paper>
      )}

      <InspectionReportDialog open={Boolean(selected)} inspection={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}
